/**
 * The light frame around My Library in the Gallery and Strips looks.
 *
 * Two tabs on top (Uploads, Exports): the two things people come here for. Search-what-was-said,
 * Meetings and Calendar move to the side list under My Library, so they stay one click away
 * without crowding the top.
 */
import React, { useCallback, useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { userContext } from "../../../../stores/users/userContext";
import { ROUTES } from "../../../../constants";
import { NAV_ITEMS } from "../../homepage/constants/navigation";
import logo from "../../../../assets/brand/logo_site_onlight.svg";
import TopBarAvatar from "../../homepage/components/TopBar/TopBarAvatar";
import AccountDropdown from "../../../account/AccountDropdown";
import AccountSettingsModal from "../../../account/AccountSettingsModal";
import { AccountTab } from "../../../account/AccountTabNav";
import { PAPER_CSS } from "./paperStyles";
import { SHELL } from "./libraryLookText";

export const TOOLS_SLOT_ID = "ccl-tools";

interface Props {
  children: React.ReactNode;
}

const LibraryShell: React.FC<Props> = ({ children }) => {
  const userStore = useContext(userContext);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  // Account settings, opened from the avatar (Profile) or from "Auto-posting" in the side list (Auto).
  const [settingsTab, setSettingsTab] = useState<AccountTab | null>(null);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const at = (path: string) => pathname === path || pathname === path.replace(/\/$/, "");
  const onUploads = at(ROUTES.MY_LIBRARY_UPLOADS) || at(ROUTES.MY_LIBRARY);
  const onExports = at(ROUTES.MY_LIBRARY_EXPORTS);

  const nav = (path: string, label: string, on: boolean, sub = false) => (
    <button type="button" className={`nav${on ? " on" : ""}${sub ? " sub" : ""}`} onClick={() => navigate(path)}>
      {label}
    </button>
  );

  return (
    <div className="ccl">
      <style>{PAPER_CSS}</style>
      <aside className="side">
        <div className="logo" onClick={() => navigate(ROUTES.HOME)} role="link" tabIndex={0}>
          <img src={logo} alt="" />
          CherryClipz
        </div>
        {nav(ROUTES.HOME, SHELL.home, false)}
        {nav(ROUTES.MY_LIBRARY_UPLOADS, SHELL.library, onUploads || onExports)}
        {nav(ROUTES.MY_LIBRARY_MEETINGS, SHELL.meetings, at(ROUTES.MY_LIBRARY_MEETINGS), true)}
        <button type="button" className="nav sub" onClick={() => setSettingsTab(AccountTab.Auto)}>{SHELL.autoPosting}</button>
        {nav(ROUTES.MY_LIBRARY_ARTISTIC, SHELL.artistic, at(ROUTES.MY_LIBRARY_ARTISTIC), true)}
        {nav(ROUTES.MY_LIBRARY_CALENDAR, SHELL.calendar, at(ROUTES.MY_LIBRARY_CALENDAR), true)}
        <div className="navhd">{SHELL.more}</div>
        <button
          type="button"
          className="nav"
          onClick={() => window.open(NAV_ITEMS.LEARNING_CENTER.path, "_blank", "noopener,noreferrer")}
        >
          {SHELL.learn}
        </button>
        {nav(NAV_ITEMS.PRICING.path, SHELL.pricing, false)}
      </aside>

      <main className="main">
        <div className="top">
          <h1>{SHELL.title}</h1>
          {userStore.isUserStripeActive() && (
            <button type="button" className="up" onClick={() => navigate("/?open=1")}>
              + {SHELL.upload}
            </button>
          )}
          <div style={{ position: "relative" }}>
            <TopBarAvatar onClick={() => setMenuOpen((o) => !o)} isActive={menuOpen} />
            <AccountDropdown
              isOpen={menuOpen}
              username={userStore.getUserFullName() || ""}
              planLabel={userStore.getUserPlan() || "FREE"}
              onAccountSettings={() => { setMenuOpen(false); setSettingsTab(AccountTab.Profile); }}
              onLogout={() => { setMenuOpen(false); userStore.logoutBackEnd(); navigate(ROUTES.LANDING); }}
              onClose={closeMenu}
            />
          </div>
        </div>
        {(onUploads || onExports) && (
          <div className="tabs">
            <button type="button" className={`tab${onUploads ? " on" : ""}`} onClick={() => navigate(ROUTES.MY_LIBRARY_UPLOADS)}>
              {SHELL.uploads}
            </button>
            <button type="button" className={`tab${onExports ? " on" : ""}`} onClick={() => navigate(ROUTES.MY_LIBRARY_EXPORTS)}>
              {SHELL.exports}
            </button>
            {/* The Uploads tab puts its few tools here (search, sort, look, ⋯), on the tabs' line. */}
            <div className="tools" id={TOOLS_SLOT_ID} />
          </div>
        )}
        {children}
      </main>
      <AccountSettingsModal open={settingsTab !== null} initialTab={settingsTab ?? undefined} onClose={() => setSettingsTab(null)} />
    </div>
  );
};

export default observer(LibraryShell);
